import React, {useEffect, useState} from 'react';
import {Container, Col, Row, Button} from 'reactstrap';
import {Location} from '@reach/router';
import queryString from 'query-string';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';
import ABTest from 'react-controlled-ab/components/ABTest';
import classNames from 'classnames';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faCarrot,
  faHeart,
  faGlobeAmericas,
  faHeadSideBrain,
  faSmile,
  faExclamationCircle,
  faTimes,
  faCartPlus,
  faLock,
  faQuestionCircle
} from '@fortawesome/pro-light-svg-icons';
import {useUserState} from 'contexts/user';
import Section, {SectionImage} from 'components/Elements/Section';
import PageMeta from 'components/PageMeta';
import Header from 'components/Header';
import Webinar from 'components/Webinar';
import Video from 'components/Video';
import CheckoutButton from 'components/CheckoutButton';
import NumberItem from 'components/NumberItem';
import {SalesCta247} from 'components/SalesCta';
import Guarantee from 'components/Guarantee';
import {TestimonialBubble} from 'components/Testimonial';
import IconList, {IconListItem} from 'components/Elements/IconList';
import ModuleAccordion, {ModuleAccordionV1} from 'components/ModuleAccordion';
import BonusList, {BonusListV1} from 'components/BonusList';
import Footer from 'components/Footer';
import ModalExitScreening from 'components/ModalExitScreening';
import RegistrationModal from 'components/RegistrationModal';
import ShareButtons from 'components/ShareButtons';
import pptLogo from 'static/logo/frn-logo-ppt.svg';
import cc from 'static/payments-accepted.png';
import track from 'utils/track';
import useBalanceText from 'utils/useBalanceText';

const COMPLETED_MASTERCLASS_EVENT = 'Completed Watching Masterclass';

export default function (props) {
  return <Location>{({location}) => <ScreeningIndex location={location} {...props} />}</Location>;
}

export function ScreeningIndex({replayOverride = false, location = {}, ...props}) {
  const {bucket, events = []} = useUserState();
  const [anchorScrolled, setAnchorScrolled] = useState(false);
  const hasCompletedMasterclass = events.includes(COMPLETED_MASTERCLASS_EVENT);

  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const {fromEntry = 'Masterclass'} = qs.fromEntry ? qs : location.state || {};

  useEffect(() => {
    const {HS_BEACON_ID} = process.env;
    if (!HS_BEACON_ID || !window.Beacon) return;
    window.Beacon('init', HS_BEACON_ID);
    return () => {
      window.Beacon('destroy');
    };
  }, []);

  useEffect(() => {
    if (hasCompletedMasterclass)
      track('Product Viewed', {
        product_id: 'ppt',
        sku: 'ppt',
        name: 'Plant-Powered & Thriving',
        variant: 'ppt-masterclass-wlc-30daytrial',
        value: 247,
        currency: 'usd'
      });
  }, [hasCompletedMasterclass]);

  useEffect(() => {
    if (anchorScrolled) return;
    if (events.includes(COMPLETED_MASTERCLASS_EVENT)) {
      setAnchorScrolled(true);
      if (
        window !== undefined &&
        window.location.hash === '#join' &&
        document.getElementById('screening-cta-thankyou')
      ) {
        document.getElementById('screening-cta-thankyou').scrollIntoView();
      }
    }
  }, [events, anchorScrolled]);

  const headlineCopy = <>10 Mighty Breakthroughs To Add Years To Your Life & Life To Your Years</>;

  const delayReveal = events.includes(COMPLETED_MASTERCLASS_EVENT) && !replayOverride;

  const webinarHeading = (
    <Row>
      <Col>
        {fromEntry === 'plants' ? (
          <>
            <h5 className="text-400 section-heading">
              Thanks for getting your <i>Guide to Going Plant-Based.</i> Good choice! (If you just ordered it, it’ll be
              in your inbox soon!)
            </h5>
            <p>Here’s what to watch next:</p>
            <h2 className="text-400 m-0">
              <strong>Food For Health</strong> <span className="text-uppercase">Masterclass</span>
            </h2>
            <h3 className="text-600 m-0">with John Robbins</h3>
            <h3 className="mt-4">
              Discover The 10 Most Important Research-Backed Breakthroughs That Can Help You Live Your Healthiest Life
            </h3>
          </>
        ) : (
          <>
            <h2 className="text-400 m-0">
              <strong>Food For Health</strong> <span className="text-uppercase">Masterclass</span>
            </h2>
            <h3 className="text-600 m-0">with John Robbins</h3>
            <h3 className="mt-4">{headlineCopy}</h3>
          </>
        )}
      </Col>
    </Row>
  );

  const webinarHeadingAfter = (
    <Row>
      <Col>
        {fromEntry === 'plants' ? (
          <>
            <h2 className="section-heading text-400 mb-0">
              <strong>Food For Health</strong> <span className="text-uppercase">Masterclass</span>
            </h2>
            <h3 className="text-600 m-0">with John Robbins</h3>
            <h3 className="mt-4">
              Discover The 10 Most Important Research-Backed Breakthroughs That Can Help You Live Your Healthiest Life
            </h3>
          </>
        ) : (
          <>
            <h2 className="text-400 m-0">
              <strong>Food For Health</strong> <span className="text-uppercase">Masterclass</span>
            </h2>
            <h3 className="text-600 m-0">with John Robbins</h3>
            <h3 className="mt-4">{headlineCopy}</h3>
          </>
        )}
      </Col>
    </Row>
  );

  const webinarFooter = (
    <Row>
      <Col>
        <h4 className="text-600 mb-0">
          <a
            href="https://cdn.foodrevolution.org/ppt/Food%20For%20Health%20MASTERCLASS%20with%20John%20Robbins%20Private%20Workbook.pdf"
            className="workbook-download text-underline"
            target="_blank"
          >
            Download your FREE workbook here.
          </a>
        </h4>
        <p className="mt-4 mb-0">
          IMPORTANT: Stay tuned ‘till the end for an amazing story <br className="d-none d-md-block" />
          from John Robbins (that he’s never shared publicly before!).
        </p>
      </Col>
    </Row>
  );

  useBalanceText();
  return (
    <div className="page d-flex flex-column">
      <PageMeta title="Join the Food For Health Masterclass with John Robbins" />

      <div className="page-content">
        <Header className="background-black header-wrap" logoWhite style="dark" linkLogo={false} />

        <Section
          id="section-watch-header"
          color="white"
          className="section-angle-first"
          innerClass={classNames(`text-center section-webinar-inner${replayOverride ? '-replay' : null}`)}
          angle="none"
        >
          <Container>
            {replayOverride ? (
              <>
                {webinarHeading}
                <Row className="d-flex justify-content-center">
                  <Col lg="10" className="video-col px-0 px-sm-3">
                    <Video url="https://vimeo.com/368609834/4e4ee6141d" label="PPT - Masterclass" />
                  </Col>
                </Row>
                {webinarFooter}
              </>
            ) : (
              <Webinar
                noun="Masterclass"
                headingSectionBefore={webinarHeading}
                headingSectionDuring={webinarHeading}
                headingSectionAfter={webinarHeadingAfter}
                footerSection={webinarFooter}
                label="PPT - Masterclass"
              />
            )}
          </Container>
        </Section>

        {delayReveal ? (
          <>
            <Section id="screening-reveal-header" color="light-gray" innerClass=" text-center" angle="both">
              <Container>
                <Row>
                  <Col>
                    <p className="mb-0">
                      Please do not close or refresh this page, as this special <br className="d-none d-sm-block" />
                      enrollment discount is only available on this page.
                    </p>
                    <h4>
                      As a thank you for attending this Masterclass, you've now unlocked a special 50% enrollment
                      discount for your seat in John & Ocean Robbins’ life-changing course, Plant-Powered & Thriving.
                    </h4>
                  </Col>
                </Row>
                <Row className="d-flex justify-content-center my-5">
                  <Col xs="12" lg="8">
                    <img className="img-fluid" src="https://cdn.foodrevolution.org/ppt/ppt-product-image.png" />
                  </Col>
                </Row>
                <Row className="d-flex justify-content-center">
                  <Col>
                    <h5 className="section-heading">Now that you know what’s at stake, it’s time to take action!</h5>
                    <p>No matter how you eat right now...</p>
                    <p>
                      You can enjoy more energy, better health, and fewer aches <br className="d-none d-md-block" />
                      and pains when you are plant-powered & thriving!
                    </p>
                    <h3 className="mt-4 mb-0">
                      Enroll Today, and You'll Get Your 50% Discount + Get the Ultimate Health Collection for FREE!
                    </h3>
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="screening-testimonial" color="white">
              <Container>
                <Row>
                  <Col>
                    <TestimonialBubble id="eric-c-v2" />
                    <hr className="my-5" />
                    <TestimonialBubble id="patsy" />
                    <hr className="my-5" />
                    <TestimonialBubble id="sheila-m" />
                    <p className="text-center text-small mt-5 mb-0 balance-text">
                      <em>
                        These stories are from people who’ve found success with our programs. Each of them is unique.
                        And so are you! Your experiences will vary, of course. And no, we never offer compensation for
                        these statements!
                      </em>
                    </p>
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section
              id="screening-cta-thankyou"
              color="gradient-indigo-purple-right"
              innerClass="text-white text-center"
            >
              <Container>
                <Row className="d-flex justify-content-center">
                  <Col lg="9" xl="10">
                    <h3 className="section-only-heading">
                      Join Food Revolution Network’s signature 6-week course and take your health to the next level.
                    </h3>
                  </Col>
                </Row>
                <Row className="d-flex justify-content-center mt-4 mb-3">
                  <Col xs="11" sm="9" md="7" lg="5" xl="4">
                    <img src="https://cdn.foodrevolution.org/ppt/ppt-logo-white.svg" />
                  </Col>
                </Row>
                <Row className="d-flex justify-content-center my-4">
                  <Col xs="10" md="8" lg="6" xl="5">
                    <img className="img-fluid" src={require('static/speaker/john-ocean-cynthia-wide.png')} />
                  </Col>
                </Row>
                <Row className="d-flex justify-content-center">
                  <Col sm="10" md="12" lg="10">
                    <h4 className="section-heading">With John & Ocean Robbins & Chef Cynthia Louise</h4>
                    <p>
                      As a thank you for watching this MASTERCLASS <br className="d-none d-md-block" />
                      Save $250 today — from this page only
                    </p>
                    <h5 className="mt-0">
                      Regular price: <del>$497</del>
                    </h5>
                    <h2 className="my-3">Now ONLY: $247</h2>
                    <p>
                      (plus you get the 20-presentation <br className="d-none d-sm-block d-xl-none" />
                      Ultimate Health Collection for FREE)
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p className="text-center my-4">
                      <CheckoutButton sku="ppt" price="297" slug="ppt-masterclass-wlc-30daytrial">
                        Join Plant-Powered & Thriving
                      </CheckoutButton>
                    </p>
                    <div className="offer-box-payment-plan">
                      Prefer a 3-month payment plan? We’ve got you covered!
                      <CheckoutButton
                        color="link"
                        className="btn-link-white ml-1"
                        sku="ppt"
                        price="89"
                        slug="ppt-masterclass-wlc-30daytrial"
                        queryParams={{opt: 1}}
                      >
                        Join now for just $89/month!
                      </CheckoutButton>
                    </div>
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="screening-boxes" color="white">
              <Container>
                <Row className="d-flex justify-content-center align-items-stretch text-center">
                  <Col xs="11" sm="10" md="6" xl="3" className="d-flex flex-column mb-5 mb-xl-0">
                    <div className="icon mb-3">
                      <FontAwesomeIcon className="text-green text-large" icon={faCarrot} />
                    </div>
                    <p className="text-16 my-auto">
                      <strong>Join Ocean, John, and Master Chef Cynthia</strong> as they share scientifically-proven
                      foods for exquisite health, longevity & joy.
                    </p>
                  </Col>
                  <Col xs="11" sm="10" md="6" xl="3" className="d-flex flex-column mb-5 mb-xl-0">
                    <div className="icon mb-3">
                      <FontAwesomeIcon className="text-green text-large" icon={faHeadSideBrain} />
                    </div>
                    <p className="text-16 my-auto">
                      <strong>Gain decades-worth of expertise</strong> packed into 6 modules, plus bonus sessions from
                      food experts. All yours to download <br className="d-none d-xl-block" />
                      and enjoy.
                    </p>
                  </Col>
                  <Col xs="11" sm="10" md="6" xl="3" className="d-flex flex-column mb-5 mb-md-0">
                    <div className="icon mb-3">
                      <FontAwesomeIcon className="text-green text-large" icon={faHeart} />
                    </div>
                    <p className="text-16 my-auto">
                      <strong>Fall in love with new favorite recipes</strong> from Master Chef Cynthia Louise that are
                      plant-based and packed with healthy nutrients.
                    </p>
                  </Col>
                  <Col xs="11" sm="10" md="6" xl="3" className="d-flex flex-column mb-5 mb-md-0">
                    <div className="icon mb-3">
                      <FontAwesomeIcon className="text-green text-large" icon={faGlobeAmericas} />
                    </div>
                    <p className="text-16 my-auto">
                      <strong>Do your part</strong> to create a sustainable planet by making choices that support your
                      health, animals, and the future of life on Earth.
                    </p>
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="ppt-testimonials" color="light-gray">
              <Container>
                <Row className="mb-5">
                  <Col>
                    <p className="text-center text-small mb-5 balance-text">
                      <i>
                        These stories are from people who’ve found success with our program. Each of them is unique. And
                        so are you! Your experiences will vary, of course. And no, we never offer compensation for these
                        statements!
                      </i>
                    </p>
                    <TestimonialBubble id="kari-z-v2" bgColor="white" />
                    <hr className="my-5" />
                    <TestimonialBubble id="ingrid-s-v2" bgColor="white" />
                    <hr className="my-5" />
                    <TestimonialBubble id="claudette" bgColor="white" />
                    <hr className="my-5" />
                    <TestimonialBubble id="betsy-g" bgColor="white" />
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="screening-know" color="white">
              <Container>
                <Row>
                  <Col>
                    <h3 className="section-heading text-center mb-2">
                      For most people, the problem isn’t that they don’t know <i>what</i> to do…
                    </h3>
                    <h3 className="text-center mt-0">
                      It’s that they don’t <i>DO</i> what they know!
                    </h3>
                    <p className="text-center">
                      Even if you already eat well most <br className="d-none d-sm-block d-lg-none" />
                      of the time, you can go even further...
                    </p>
                  </Col>
                </Row>
                <Row className="d-flex justify-content-center">
                  <Col lg="10">
                    <h5>
                      And take an even <em>bigger</em> step away from eating:
                    </h5>
                    <IconList iconColor="red">
                      <IconListItem icon={faTimes}>
                        <strong>added sugars</strong> which are the #1 contributor to type 2 diabetes, weight gain, and
                        liver disease in the United States
                      </IconListItem>
                      <IconListItem icon={faTimes}>
                        <strong>meat and dairy</strong> products that cause heart disease, inflammation, cancer, and a
                        host of other diseases — along with environmental devastation
                      </IconListItem>
                      <IconListItem icon={faTimes}>
                        <strong>highly-processed</strong> “healthy” or “natural” foods that actually leave you deficient
                        in key nutrients
                      </IconListItem>
                    </IconList>
                    <h5>And fall in LOVE with eating healthy foods like:</h5>
                    <IconList>
                      <IconListItem>
                        <strong>leafy greens</strong> that are packed with vitamins and minerals (We’ll show you how to
                        make collards taste amazing)
                      </IconListItem>
                      <IconListItem>
                        <strong>berries</strong> loaded with antioxidants and anti-cancer properties (Berries are good
                        for your brain, your heart, and every cell in your body)
                      </IconListItem>
                      <IconListItem>
                        <strong>nuts and seeds</strong> packed with essential nutrients that your body needs to function
                        (You’ll learn new and exciting ways to enjoy these superfoods.)
                      </IconListItem>
                    </IconList>
                  </Col>
                </Row>
              </Container>
            </Section>

            <SectionImage
              id="screening-news"
              color="light-gray"
              imgPosition="right"
              imgUrl={require('static/backgrounds/elderly-swinging-square.jpg')}
              title={
                <h4 className="section-only-heading">
                  During the masterclass, you found out how the status quo is a recipe for suffering: for you, your
                  loved ones, animals, future generations, and the planet.
                </h4>
              }
              titleClass="mb-5"
            >
              <p>But trying to change on your own can be difficult, especially if you are already experiencing:</p>
              <ul>
                <li>low energy</li>
                <li>lack of motivation</li>
                <li>confusion about what foods are actually healthy</li>
                <li>brain fog</li>
                <li>aches & pains</li>
                <li>digestive issues</li>
                <li>trouble sleeping well</li>
                <li>food sensitivities/allergies</li>
                <li>debilitating or chronic illness</li>
              </ul>
              <h4>The good news is…</h4>
              <p>If you start right now, you can quickly feel more energy, productivity, and joy…</p>
              <p>Imagine how good it will feel to inspire yourself and your family to eat healthier...</p>
              <p>
                And to do good for the environment so that future generations can enjoy a healthy planet — rather than
                continued deforestation, pesticides, antibiotics, and pollution.
              </p>
              <p>
                It’s time to take your future into your own hands instead of leaving it to Big Pharma and expensive
                medical treatments that might or might not work.
              </p>
            </SectionImage>

            <Section id="ppt-intro" color="white">
              <Container>
                <Row className="d-flex justify-content-center">
                  <Col lg="10">
                    <h2 className="section-heading text-center">Introducing...</h2>
                    <Row className="d-flex justify-content-center">
                      <Col xs="11" sm="9" md="7" lg="5" xl="4">
                        <h2 className="section-heading">
                          <img className="img-fluid" src={pptLogo} />
                        </h2>
                      </Col>
                    </Row>
                    <Row className="d-flex justify-content-center">
                      <Col xs="12" lg="8">
                        <img className="img-fluid" src="https://cdn.foodrevolution.org/ppt/ppt-product-image.png" />
                      </Col>
                    </Row>
                    <h3 className="section-heading text-400 text-center">a hands-on 6-week program</h3>
                    <h4 className="text-center">Plant-Powered & Thriving is more than just a course.</h4>
                    <h5 className="text-400 text-center mt-0 mb-5">
                      It’s a shortcut to the healthiest version of yourself!
                    </h5>
                    <NumberItem number="1" title="Learn to crave kale instead of junk food">
                      Junk food can lead to fatigue, brain fog, cancer, heart disease, and Alzheimer’s — even if it’s
                      vegan, dairy-free, and gluten-free!
                    </NumberItem>
                    <NumberItem number="2" title="Avoid toxins that fuel disease">
                      Are you eating as much organic as possible? Conventionally-grown food contains alarming amounts of
                      toxic, chemical pesticides which may increase your risk of diseases like cancer.
                    </NumberItem>
                    <NumberItem number="3" title="End food confusion and fads once and for all">
                      Do you know if you’re getting your nutritional needs met in the healthiest way possible? We’ll
                      show you the latest research.
                    </NumberItem>
                    <NumberItem number="4" title="Watch excess weight slip off effortlessly...">
                      Packing your plate with delicious, plant-based meals is the easiest way to eat the right amount of
                      food for your body. Unlike any other crash diet on the planet, the weight never returns.
                    </NumberItem>
                    <NumberItem number="5" title="Become a positive role model for your friends and family">
                      Set a shining example for those around you as you make wonderful memories over deliciously healthy
                      meals everyone will love.
                    </NumberItem>
                    <NumberItem number="6" title="Dine out smartly">
                      Be able to CONFIDENTLY select healthier fare when eating out — while never feeling like you’re
                      missing out!
                    </NumberItem>
                    <NumberItem number="7" title="Save hundreds, even thousands of dollars every year">
                      It hurts to throw food out — and it’s 100% avoidable with just a little planning. (PLUS: you’ll
                      make fewer trips to the grocery store.)
                    </NumberItem>
                    <NumberItem number="8" title="Enjoy more time to do the things you love with people you love">
                      Good planning means less time scrambling to figure out what’s for dinner and more quality time to
                      spend with loved ones, relaxing with a good book, or outdoors.
                    </NumberItem>
                    <h4 className="text-center">
                      Best of all, a healthy diet means less time spent dealing with medical issues, at doctors’{' '}
                      offices, pharmacies, and insurance companies!
                    </h4>
                    <p className="text-center mb-0">
                      Watch over our shoulders every step of the way as we show <br className="d-none d-xl-block" />
                      you how to go from just <em>wishing</em> you would eat better — to{' '}
                      <br className="d-none d-xl-block" />
                      implementing, perfecting, and loving what you learn!
                    </p>
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section
              id="ppt-personal-note"
              className="background-image-wood-panel background-image-left-bottom background-cover background-fixed"
              color="white"
              angle="none"
              innerClass="section-inner-noangle-offset"
            >
              <Container>
                <Row>
                  <Col>
                    <div className="background-white px-3 py-4 px-lg-5 py-lg-5 my-3">
                      <Row className="d-flex align-items-center justify-content-center mb-lg-4">
                        <Col xs="12" sm="8" lg="4" xl="3" className="order-lg-2 text-center mb-4 mb-lg-0">
                          <img
                            className="img-ocean-note rounded-circle"
                            src={require('static/speaker/ocean-robbins.jpg')}
                          />
                        </Col>
                        <Col xs="12" lg="8" xl="9" className="order-lg-1">
                          <h2 className="section-heading mb-4">A personal note from Ocean Robbins</h2>
                          <h4 className="my-4">
                            In his 70s, my Grandpa Irv (who founded Baskin-Robbins ice cream company) became seriously
                            ill...
                          </h4>
                        </Col>
                      </Row>
                      <Row className="d-flex align-items-center justify-content-center">
                        <Col xs="12" sm="8" lg="4" xl="3" className="img-ocean-note-icecream-row text-center">
                          <img
                            className="img-ocean-note img-ocean-note-icecream img-fluid img-rotate-left"
                            src={require('static/irvine-with-ice-cream.jpg')}
                          />
                        </Col>
                        <Col xs="12" lg="8" xl="9">
                          <p>
                            <strong>
                              He’d always eaten the standard American diet, plus a double scoop of ice cream on top, and
                              now he was suffering from the standard American diseases.
                            </strong>
                          </p>
                          <h5 className="my-4">That was his wake-up call.</h5>
                          <p>
                            His doctor told him that he'd have to take seven medications three times every day for the
                            rest of his life and that unless he changed his diet, he likely had only a few years to live
                            — in poor health.
                          </p>
                          <p>And then his doctor gave Grandpa Irv a book and told him to read it.</p>
                        </Col>
                      </Row>
                      <p>
                        The book was already familiar to him because it was written by his son (my dad), John Robbins.
                        But he hadn’t read it.
                      </p>
                      <p>
                        You see, there were some hard feelings in the family. Decades earlier, my grandpa had been
                        grooming my dad, John, to one day join in the family business.
                      </p>
                      <p>
                        But when he was in his early 20’s, my dad wound up walking away from Baskin-Robbins and the
                        wealth it represented because it didn't align with his values of health and compassion.
                      </p>
                      <p>
                        My dad left that legacy to follow his own "rocky road" and become a best-selling food and health
                        author. The media had fun with the story, calling him the “rebel without a cone.”
                      </p>
                      <p>
                        Millions of people were inspired by his work — including, as fate would have it, Grandpa Irv’s
                        doctor, who was now recommending my dad's best-selling book, Diet for a New America, to his
                        patient.
                      </p>
                      <Row className="d-flex justify-content-center">
                        <Col xs="12" lg="7" xl="8">
                          <p>And amazingly enough, Grandpa Irv read the book and made some big changes.</p>
                          <p>
                            He added lots of vegetables and fruits; he went plant-powered, and he even gave up ice
                            cream!
                          </p>
                          <h4 className="my-4">
                            My Grandpa Irv ended up getting off all his medications and adding almost 20 more healthy
                            years to his life.
                          </h4>
                        </Col>
                        <Col xs="12" sm="8" lg="5" xl="4" className="img-ocean-note-suits-row text-center">
                          <img
                            className="img-ocean-note img-ocean-note-suits img-fluid img-rotate-right"
                            src={require('static/irvine-john-ocean.jpg')}
                          />
                        </Col>
                      </Row>

                      <Row className=" d-flex justify-content-center align-items-center">
                        <Col xs="12" lg="8" className="order-lg-2">
                          <p>
                            The great news is that this is the kind of transformation I know is possible for anyone,
                            including you and your loved ones.
                          </p>
                          <h4 className="my-4">That's how POWERFUL food is.</h4>
                          <p>
                            My dad and I have spent decades living our passion to empower millions of people with this
                            life-saving knowledge.
                          </p>
                        </Col>
                        <Col xs="12" sm="8" lg="4" className="img-ocean-note-flexing-row text-center order-lg-1">
                          <img
                            className="img-ocean-note img-ocean-note-flexing img-fluid rounded-circle"
                            src={require('static/speaker/john-ocean-square.jpg')}
                          />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="ppt-hosts" color="white" angle="both">
              <Container>
                <Row>
                  <Col>
                    <h1 className="section-only-heading text-center">Meet Your Hosts</h1>
                  </Col>
                </Row>
              </Container>
            </Section>

            <SectionImage
              id="ppt-host-john"
              color="black"
              imgPosition="right"
              innerClass="text-white"
              imgUrl={require('static/speaker/john-robbins-veggies-square.jpg')}
              imgUrlMobile={require('static/speaker/john-robbins-veggies-square.jpg')}
            >
              <h3 className="section-heading">John Robbins</h3>
              <p>
                John Robbins could have inherited the multi-billion dollar Baskin-Robbins empire. But he walked away
                from the money and the power. Why? He simply didn’t want to devote his life to selling ice cream after
                realizing it makes people unhealthy. So, he decided to make a change.
              </p>
              <p>
                Over the last several decades, his books about healthy eating and healthy living (including bestsellers,{' '}
                <em>Diet for a New America</em> and <em>The Food Revolution</em>) have sold millions of copies and been
                translated into more than 31 languages.
              </p>
              <p>
                In the 5 years after the publication of his first best-selling book, feedlot beef consumption in the
                United States dropped by over 20%. (A representative for the National Cattlemen’s Beef Association,
                speaking on NPR, blamed John Robbins!)
              </p>
              <p>
                Hundreds of thousands of people consider his work responsible for transforming their health. He’ll
                provide seasoned wisdom and steady guidance you can trust.
              </p>
            </SectionImage>

            <SectionImage
              id="ppt-host-ocean"
              color="black"
              imgPosition="left"
              innerClass="text-white"
              imgUrl={require('static/speaker/ocean-robbins-veggies-square.jpg')}
              imgUrlMobile={require('static/speaker/ocean-robbins-veggies-square.jpg')}
            >
              <h3 className="section-heading">Ocean Robbins</h3>
              <p>
                The food revolution has been in Ocean’s blood since before he was born. He grew up in a log cabin built
                by his parents John and Deo Robbins, eating food they grew on the land together.
              </p>
              <p>
                At the age of 10, he started a natural foods bakery and ran his first marathon. By 15 he was speaking at
                the United Nations and opening for the Jerry Garcia Band.
              </p>
              <p>
                Over the last several decades he has spoken in person to more than 250,000 people in 65+ nations and
                hosted online events for millions. He’s the author of the bestselling book,{' '}
                <em>31-Day Food Revolution: Heal Your Body, Feel Great, & Transform Your World,</em> and has served as
                an adjunct professor at Chapman University.
              </p>
              <p>
                Ocean wanted to make the food revolution reach everyone and to make it easy and accessible for people
                from all walks of life. So in 2012, he founded Food Revolution Network with his dad, John Robbins, and
                has since guided millions of people to a healthier way of eating and living.
              </p>
              <p>
                No matter where you’re starting from, Ocean’s warmth and expertise will carry you through the course
                every step of the way.
              </p>
            </SectionImage>

            <SectionImage
              id="ppt-host-cynthia"
              color="black"
              imgPosition="right"
              innerClass="text-white"
              imgUrl={require('static/speaker/cynthia-louise-square.jpg')}
              imgUrlMobile={require('static/speaker/cynthia-louise-square.jpg')}
            >
              <h3 className="section-heading">Chef Cynthia Louise</h3>
              <p>
                Chef Cynthia has created 3 best-selling cookbooks and has been a savior to many households around the
                globe. Her genius is taking traditional favorite foods and transforming them into simple healthy dishes
                with maximum nutrition and flavor.
              </p>
              <p>
                Chef Cynthia grew up in Papua New Guinea, studied at the top health retreats in Australia, and is a
                Qualified Wholefood Chef based in beautiful Bali, Indonesia, who focuses on vegetarian and dairy-free
                food and drinks. She brings a fun, global perspective to cuisine and a passion for healthy food that is
                irresistible!
              </p>
              <p>
                With so much love infused in everything she does, it’s no surprise that her e-books, Bali retreats, and
                online cooking classes are always a huge success. Her playful approach to cooking makes time spent in
                the kitchen simple, fast, and delightful.
              </p>
              <p>
                She’ll help you whip up genuine joy as you prepare healthy meals that are excellent for you, your
                family, animals, and the planet!
              </p>
            </SectionImage>

            <Section id="ppt-included" color="white">
              <Container>
                <Row className="d-flex justify-content-center">
                  <Col>
                    <h2 className="section-heading text-center mb-0">How This Program Will Help You Succeed</h2>
                    <ul className="list-hr mt-5">
                      <li>
                        <h5 className="mt-0 mb-2">John, Ocean, and Chef Cynthia</h5>
                        <p className="mb-0">
                          No more trying to do it all alone. Together, we are 3 highly sought-after food and health
                          educators, and we’re honored to be your guides on this life-changing adventure.
                        </p>
                      </li>
                      <li>
                        <h5 className="mt-0 mb-2">Easy-to-follow modules</h5>
                        <p className="mb-0">
                          Ditch the complicated instructions! With every lesson, you’ll have the option to watch the
                          high definition video or listen to an MP3 version while you’re jogging, folding the laundry,
                          or driving to work. Or if you prefer you can read the transcript.
                        </p>
                      </li>
                      <li>
                        <h5 className="mt-0 mb-2">Cooking Sessions</h5>
                        <p className="mb-0">
                          Ready for your new favorite recipes? Discover how to whip up nutritious foods that taste
                          amazing and get PROVEN SHORTCUTS to prepare nutrient-packed meals when you’re in a hurry.
                        </p>
                      </li>
                      <li>
                        <h5 className="mt-0 mb-2">Private community (NOT Facebook!)</h5>
                        <p className="mb-0">
                          No slipping through the cracks — this course is interactive. Receive expert guidance for all
                          your questions, stay in the loop about new scientific studies, and meet friends who will
                          inspire you to do your best!
                        </p>
                      </li>
                      <li>
                        <h5 className="mt-0 mb-2">
                          <span className="text-purple">BONUS:</span> Extra Trainings
                        </h5>
                        <p className="mb-0">
                          This course comes with a collection of free bonus trainings from world-renowned nutritional
                          experts and leaders — a collection only available here!
                        </p>
                      </li>
                      <li>
                        <h5 className="mt-0 mb-2">
                          <span className="text-purple">BONUS:</span> Q&A Sessions
                        </h5>
                        <p className="mb-0">
                          John Robbins, Ocean Robbins, & Chef Cynthia answer the TOP questions from our community to
                          save you time and hassle so you can avoid costly mistakes and see — <i>and feel</i> — results
                          quickly (pre-recorded and available instantly).
                        </p>
                      </li>
                      <li>
                        <h5 className="mt-0 mb-2">
                          <span className="text-purple">BONUS (On this page ONLY!):</span> Ultimate Health Collection
                        </h5>
                        <p className="mb-0">
                          Get 20 of the most popular interviews between John and the world’s leading food experts as
                          they share the most critical food and health developments you need to protect yourself.
                        </p>
                      </li>
                    </ul>
                  </Col>
                </Row>
                <Row className="mt-5">
                  <Col className="mt-5">
                    <TestimonialBubble id="cheryl-s-v2" />
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="" color="light-gray">
              <Container>
                <Row className="d-flex justify-content-center">
                  <Col lg="10">
                    <h3 className="section-heading">
                      Do you want to maximize your energy, lose excess weight with minimal effort, and help the world be
                      a better place all at the same time?
                    </h3>
                    <p>
                      Of course, we ALL want to do our best. But most people are still failing to eat as many veggies as
                      they should be — and are eating more sugar, animal products, and processed foods than they would
                      like.
                    </p>
                    <p>
                      Here are the <strong>biggest challenges</strong> we hear.
                    </p>
                    <IconList iconColor="red">
                      <IconListItem icon={faExclamationCircle}>
                        <h6 className="section-heading mb-1">Challenge 1</h6>
                        <p className="mb-0">
                          I eat well 85% of the time, but I can’t seem to keep myself away from the bag of cookies or
                          potato chips. (Is it less bad if they’re organic?)
                        </p>
                      </IconListItem>
                      <IconListItem icon={faExclamationCircle}>
                        <h6 className="section-heading mb-1">Challenge 2</h6>
                        <p className="mb-0">
                          I was on a great health kick, but then I fell off the wagon and I settle for what’s convenient
                          more than I like to admit.
                        </p>
                      </IconListItem>
                      <IconListItem icon={faExclamationCircle}>
                        <h6 className="section-heading mb-1">Challenge 3</h6>
                        <p className="mb-0">
                          I’m worried that I won’t get enough protein, iron, or other important nutrients if I eat a
                          plant-based diet. Will I do damage to myself or my children?
                        </p>
                      </IconListItem>
                      <IconListItem icon={faExclamationCircle}>
                        <h6 className="section-heading mb-1">Challenge 4</h6>
                        <p className="mb-0">How do I eat healthy when the other people around me don’t?</p>
                      </IconListItem>
                      <IconListItem icon={faExclamationCircle}>
                        <h6 className="section-heading mb-1">Challenge 5</h6>
                        <p className="mb-0">I’m so busy — how will I find the time to cook the way I know I need to?</p>
                      </IconListItem>
                    </IconList>
                    <p>
                      When we talk to people who say they want to eat healthier, we hear about the same problems time
                      and again.
                    </p>
                    <p>
                      And our solution is one that we’ve proven to work for almost everyone, from beginners to seasoned
                      cooks who want to make the big shift to Plant-Powered & Thriving!
                    </p>
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="ppt-modules" color="white">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading mb-5">How We Will Make You a Seasoned Plant-Powered Pro</h2>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <ModuleAccordionV1 />
                  </Col>
                </Row>
                <Row className="mt-5">
                  <Col>
                    <h3 className="text-center mb-5">Is Plant-Powered & Thriving worth the investment?</h3>
                    <TestimonialBubble id="linda-b" />
                    <hr className="my-5" />
                    <TestimonialBubble id="denise-a-v2" />
                    <hr className="my-5" />
                    <TestimonialBubble id="france-t" />
                    <hr className="my-5" />
                    <TestimonialBubble id="shayne-t" />
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="ppt-special" color="light-gray">
              <Container>
                <Row className="d-flex justify-content-center">
                  <Col lg="10">
                    <h2 className="section-heading">About Today's Special Early-Bird Enrollment Discount</h2>
                    <p>
                      This early-bird discount is only for Masterclass attendees — as a reward for investing an hour in
                      your growth by watching the Masterclass. You can unlock a special price only on this page.
                    </p>
                    <p>
                      With this Special Masterclass Price, you save $250 by enrolling in Plant-Powered & Thriving today.
                    </p>
                    <p className="mb-0">
                      IMPORTANT! Only order from this page to get our best offer. The enrollment price is higher
                      everywhere else on our site.
                    </p>
                  </Col>
                </Row>
                <Row className="d-flex justify-content-center my-5">
                  <Col xs="12" lg="8">
                    <img className="img-fluid" src="https://cdn.foodrevolution.org/ppt/ppt-product-image.png" />
                  </Col>
                </Row>
                <Row className="d-flex justify-content-center">
                  <Col lg="10">
                    <h2 className="section-heading">Why Order Today: 3 Powerful Advantages Only Available Now</h2>
                    <h3>1. The Lowest Price That Will Ever Be Offered.</h3>
                    <p>
                      The regular price for Plant-Powered & Thriving is $497. And for all that it includes, that’s
                      already a rock bottom price! But when you enroll today, you'll invest only $247 — this is a
                      MASSIVE $250 discount. (Prefer a three-month payment plan? We’ve got you covered! Join now for
                      just $89/month!)
                    </p>
                    <p>
                      You just chose to invest your time in our Masterclass, and this means you’re probably already one
                      of the special people who know that food matters, and who’s willing to eat differently, and to
                      think differently, than the vast majority of the people around you. In other words, you’re exactly
                      the kind of person we want in this program so that we can accomplish our goal of mobilizing
                      millions for healthy people and a healthy planet!
                    </p>
                    <p>So we want to make it easy for you to say yes, and to say yes, now.</p>
                    <h3>2. Get the Ultimate Health Collection as a totally free bonus.</h3>
                    <p>
                      Since 2012, John Robbins has personally interviewed hundreds of the world’s leading food experts
                      in our annual Food Revolution Summits. We compiled 20 of the most popular interviews he’s ever
                      conducted on the topics of food and health and put them into this Ultimate Health Collection.
                      Experts include Michael Pollan, Rip Esselstyn, Dr. Dale Bredesen, Dan Buettner, Dr. T. Colin
                      Campbell, Kris Carr, Dr. Kim Williams, Tony Robbins, and more! The only other way to directly buy
                      this curated collection would be to purchase the Empowerment Package from all of our past Food
                      Revolution Summits and to spend more than $2,000 in the process. But it’s a totally free bonus
                      only when you sign up right here. And it comes with streaming access, plus downloadable MP3s and
                      transcripts, from all 20 brilliant presentations.
                    </p>
                    <h3>3. Join With Other Participants Who Are Ready Now, Too</h3>
                    <p>
                      The best way to ensure that your healthy intentions become reality is to join a community on the
                      same mission as you!
                    </p>

                    <IconList iconColor="purple">
                      <IconListItem icon={faSmile}>
                        Receive answers from our health coaches and nutritionists anytime you have a question — this is
                        worth the cost of the program alone!
                      </IconListItem>
                      <IconListItem icon={faSmile}>
                        Get the latest health updates and groundbreaking scientific studies.
                      </IconListItem>
                      <IconListItem icon={faSmile}>
                        Share recipes, struggles, successes, and stay inspired by those who share your goal to be
                        healthy.
                      </IconListItem>
                    </IconList>
                    <p className="text-center mt-5">
                      This is the warmest and most engaged moderated online <br className="d-none d-lg-block" />
                      community you can find. (And it’s NOT on Facebook.)
                    </p>
                    <h5 className="mt-0 text-center">Your success depends on the company you keep!</h5>
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="ppt-bonuses" color="white">
              <Container>
                <Row className="d-flex justify-content-center">
                  <Col>
                    <h2 className="section-heading text-center">
                      And There’s Even More! Enroll Now For These Powerful Bonuses
                    </h2>
                    <p className="text-center">
                      When you join Plant-Powered & Thriving, <br className="d-none d-sm-block d-md-none" />
                      you’ll gain access to a <br className="d-none d-lg-block" />
                      powerful collection of <br className="d-none d-sm-block d-md-none" />
                      free bonuses from our friends and partners!
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <BonusListV1 />
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="" color="light-gray">
              <Container>
                <Row>
                  <Col className="my-5">
                    <TestimonialBubble id="marium-a-v2" bgColor="white" />
                    <hr className="my-5" />
                    <TestimonialBubble id="rhonda-h-v2" bgColor="white" />
                    <hr className="my-5" />
                    <TestimonialBubble id="chuck-g-v2" bgColor="white" />
                    <hr className="my-5" />
                    <TestimonialBubble id="cecile-n-v2" bgColor="white" />
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="" color="white">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading text-center mb-0">JOIN NOW</h2>
                    <Row className="d-flex justify-content-center my-4">
                      <Col xs="12" lg="8">
                        <img className="img-fluid" src="https://cdn.foodrevolution.org/ppt/ppt-product-image.png" />
                      </Col>
                    </Row>
                    <p className="text-center">
                      <strong>Get instant access — even at 2 am – guaranteed!</strong>
                    </p>
                    <div className="offer-box-offer-text text-center">
                      Masterclass Special <br className="d-none d-sm-block d-lg-none" />
                      Just <del className="regular-price">$497</del> $247
                    </div>
                    <div className="offer-box-payment-plan text-center">
                      Prefer a 3-month payment plan? <br />
                      We’ve got you covered!
                      <CheckoutButton
                        color="link"
                        className="btn-link-checkout ml-1"
                        sku="ppt"
                        price="89"
                        slug="ppt-masterclass-wlc-30daytrial"
                        queryParams={{opt: 1}}
                      >
                        Join now for just $89/month!
                      </CheckoutButton>
                    </div>
                  </Col>
                </Row>
                <Row className="d-flex justify-content-center">
                  <Col lg="10">
                    <IconList className="my-5">
                      <IconListItem>
                        6 modules, each with video lessons from John Robbins, Ocean Robbins, and Chef Cynthia.
                      </IconListItem>
                      <IconListItem>
                        All lessons are available in HD video. MP3s and transcripts of the lessons with John and Ocean
                        also available for easy download.
                      </IconListItem>
                      <IconListItem>
                        Access to an Exclusive Forum (that’s NOT on Facebook) where you can ask experts your questions
                        and work through any challenges.
                      </IconListItem>
                      <IconListItem>
                        6 Fabulous & Easy-To-Prepare Recipes Every Week — Soy-Free, GMO-Free, Gluten-Free.
                      </IconListItem>
                      <IconListItem>
                        The basic recipe collection is 100% plant-based and non-GMO with gluten-free and soy-free
                        options that are super easy to implement.
                      </IconListItem>
                      <IconListItem>
                        7 delightful recipe preparation videos with chef Cynthia (see exactly how she does it!).
                      </IconListItem>
                      <IconListItem>12 Potent Special Reports.</IconListItem>
                      <IconListItem>
                        Guest Faculty Interviews with Dr. Dean Ornish, Kathy Freston, Dr. Joel Fuhrman, Brenda Davis,
                        Dr. Caldwell Esselstyn, and Michael Bernard Beckwith.
                      </IconListItem>
                      <IconListItem>
                        Q&A with John Robbins, Ocean Robbins, & Chef Cynthia – where we answer the TOP questions from
                        our community (pre-recorded).
                      </IconListItem>
                      <IconListItem>BONUS: G-BOMBS e-book by Joel Fuhrman, MD</IconListItem>
                      <IconListItem>
                        BONUS: Ornish Lifestyle Medicine: An Implementation Guidebook by Dean Ornish, MD
                      </IconListItem>
                      <IconListItem>
                        BONUS: Health & Cancer: How to Take Charge of Your Healing Journey by Caryn Hartglass
                      </IconListItem>
                      <IconListItem>BONUS: Dessert Plantz eCookbook by Plant-Based Katie Mae</IconListItem>
                      <IconListItem>
                        BONUS: Eat Your Way Out of Pain Home Study Program by Dr. Ritamarie Loscalzo
                      </IconListItem>
                      <IconListItem>BONUS: The Dairy-Freedom Cookbook by Allison Rivers Samson</IconListItem>
                      <IconListItem>BONUS (Now Only!): The Ultimate Health Collection</IconListItem>
                    </IconList>
                  </Col>
                </Row>
                <Row>
                  <Col className="text-center">
                    <p>
                      <strong>Unlimited Access.</strong>
                    </p>
                    <p className="mb-4">
                      <strong>Download & enjoy it all as many times as you want.</strong>
                    </p>
                    <CheckoutButton
                      className="offer-box-button"
                      sku="ppt"
                      price="247"
                      slug="ppt-masterclass-wlc-30daytrial"
                    >
                      <FontAwesomeIcon className="mr-2" icon={faCartPlus} /> Buy Now
                    </CheckoutButton>
                    <div className="offer-box-details">
                      <div className="offer-box-cards">
                        <img className="img-fluid img-cc" src={cc} />
                      </div>
                      <div className="offer-box-security">
                        <FontAwesomeIcon className="text-green mr-2" icon={faLock} />
                        For your security, all orders are processed on a secured server.
                      </div>
                    </div>
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="" color="light-gray">
              <Container>
                <Row>
                  <Col>
                    <TestimonialBubble id="terri-c-v2" bgColor="white" />
                    <hr className="my-5" />
                    <TestimonialBubble id="shelley" bgColor="white" />
                    <hr className="my-5" />
                    <TestimonialBubble id="gretchen" bgColor="white" />
                    <hr className="my-5" />
                    <TestimonialBubble id="pat" bgColor="white" />
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="ppt-join-cta" color="gradient-indigo-purple-right">
              <Container>
                <Row className="text-white text-center">
                  <Col>
                    <h3 className="section-only-heading">
                      Join Food Revolution Network’s 6-week mentorship program for the best health of your life
                    </h3>
                    <Row className="d-flex justify-content-center my-4">
                      <Col xs="10" md="8" lg="6" xl="5">
                        <img className="img-fluid" src={require('static/speaker/john-ocean-cynthia-wide.png')} />
                      </Col>
                    </Row>
                    <h4 className="mt-0">With John & Ocean Robbins & Chef Cynthia Louise</h4>
                    <p>
                      As a thank you for watching this MASTERCLASS <br className="d-none d-sm-block d-xl-none" />
                      Save $250 today — from this page only
                    </p>
                  </Col>
                </Row>
                <Row className="mt-4">
                  <Col>
                    <SalesCta247 />
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="ppt-guarantee" color="light-gray" className="section-angle-last" angle="none">
              <Container>
                <Row>
                  <Col>
                    <Guarantee />
                  </Col>
                </Row>
              </Container>
            </Section>
          </>
        ) : null}

        {replayOverride ? (
          <Section
            id="replay-cta"
            color="gradient-indigo-purple-right"
            innerClass="text-center text-white section-webinar-inner-cta"
            angle="none"
          >
            <Container>
              <Row>
                <Col>
                  <h4 className="section-heading">
                    Are you interested in enrolling in John & Ocean Robbins’ course, Plant-Powered & Thriving?
                  </h4>
                  <h5>Click below to learn more:</h5>
                  <Button className="btn-join btn-lg" color="cta" href="/join/" target="_blank">
                    Join Now!
                  </Button>
                </Col>
              </Row>
            </Container>
          </Section>
        ) : (
          <>
            <RegistrationModal />
            <ModalExitScreening />
          </>
        )}
      </div>

      <div className="footer-content mt-auto">
        <Footer />
        <ShareButtons floating />
      </div>
    </div>
  );
}
